import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { formatCurrency } from '../../utils/formatters';
import { useOrderType, ORDER_TYPES } from '../../context/OrderTypeContext';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useFetchData } from 'services/queries';
import { NCMFormatter } from 'utils';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { DataGridPremium } from '@mui/x-data-grid-premium';

/**
 * ProductTable - Displays product information specific to order type
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 * @param {number} props.exchangeRate - The USD to BRL exchange rate
 */
const ProductTable = ({ order, exchangeRate }) => {
  const { orderType } = useOrderType();
  const { data: products, error, isLoading } = useFetchData(`order/product-item-tax/?sales_order=${order.id}`, true);

  if (!order) return null;

  if (isLoading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Inventory2Icon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">PRODUCTS</Typography>
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Inventory2Icon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">PRODUCTS</Typography>
          </Box>
          <Alert severity="error">Error! {error.message}</Alert>
        </CardContent>
      </Card>
    );
  }

  // If no products, show empty state
  if (!products || products.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Inventory2Icon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">PRODUCTS</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            No products to display.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Define columns based on order type
  const getColumns = () => {
    const baseColumns = [
      {
        field: 'product',
        headerName: 'PRODUCT',
        flex: 2.5,
        minWidth: 350,
        renderCell: (params) => {
          const product = params.row;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', py: 0.5 }}>
              {/* Avatar/Badge Section */}
              <Box sx={{ mr: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                {/* Tooltip added around the Badge */}
                <Tooltip title={`Quantity: ${product.quantity}`} placement="top">
                  {product.product_picture ? (
                    <Badge
                      badgeContent={
                        <Chip
                          size="small"
                          label={product.quantity}
                          color="primary"
                          sx={{
                            height: 18,
                            minWidth: 24,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      }
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                    >
                      <Avatar
                        src={product.product_picture}
                        alt={product.product_name}
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 48,
                          boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
                          border: '1px solid #eee'
                        }}
                      />
                    </Badge>
                  ) : (
                    <Badge
                      badgeContent={
                        <Chip
                          size="small"
                          label={product.quantity}
                          color="primary"
                          sx={{
                            height: 18,
                            minWidth: 24,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      }
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: 'grey.100',
                          boxShadow: '0px 1px 3px rgba(0,0,0,0.1)'
                        }}
                      >
                        {product.product_name ? product.product_name.charAt(0) : ' '}
                      </Avatar>
                    </Badge>
                  )}
                </Tooltip>
              </Box>
              {/* Text Details Section - Vertically Centered */}
              <Box sx={{ overflow: 'hidden', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Tooltip title={product.product_name} placement="top-start">
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.3,
                      mb: 0.75 // Margin below name
                    }}
                  >
                    {product.product_name || '-'}
                  </Typography>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Chip
                    label={product.ncm ? NCMFormatter(product.ncm) : 'N/A'}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                      '& .MuiChip-label': {
                        px: 0.6
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        }
      },
      {
        field: 'unit_of_measure',
        headerName: 'UNIT',
        flex: 0.7,
        minWidth: 90,
      },
      {
        field: 'invoice_usd', // Field containing FOB USD
        headerName: 'FOB',
        flex: 1,
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const valueUSD = parseFloat(params.value) || 0;
          const valueBRL = valueUSD * parseFloat(exchangeRate || 1);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              <Stack spacing={0.2} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  {formatCurrency(valueBRL, 'BRL')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                  {formatCurrency(valueUSD, 'USD')}
                </Typography>
              </Stack>
            </Box>
          );
        }
      },
      {
        field: 'cfr', // Field containing CFR USD
        headerName: 'CFR',
        flex: 1,
        minWidth: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const valueUSD = parseFloat(params.value) || 0;
          const valueBRL = valueUSD * parseFloat(exchangeRate || 1);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              <Stack spacing={0.2} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  {formatCurrency(valueBRL, 'BRL')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                  {formatCurrency(valueUSD, 'USD')}
                </Typography>
              </Stack>
            </Box>
          );
        }
      },
    ];

    let columns = baseColumns;

    if (
      orderType === ORDER_TYPES.ADVISORY ||
      orderType === ORDER_TYPES.ACCOUNT_AND_ORDER ||
      orderType === ORDER_TYPES.ORDER
    ) {
      columns = [
        ...baseColumns,
        {
          field: 'unit_disbursement', // Field containing Unit Disbursement BRL
          headerName: 'UNIT DISBURSEMENT',
          flex: 1,
          minWidth: 90,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const valueBRL = parseFloat(params.value) || 0;
            const valueUSD = valueBRL / parseFloat(exchangeRate || 1);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                <Stack spacing={0.2} sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {formatCurrency(valueBRL, 'BRL')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                    {formatCurrency(valueUSD, 'USD')}
                  </Typography>
                </Stack>
              </Box>
            );
          }
        },
        {
          field: 'unit_cost', // Field containing Unit Cost BRL
          headerName: 'UNIT COST',
          flex: 1,
          minWidth: 90,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const valueBRL = parseFloat(params.value) || 0;
            const valueUSD = valueBRL / parseFloat(exchangeRate || 1);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                <Stack spacing={0.2} sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {formatCurrency(valueBRL, 'BRL')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                    {formatCurrency(valueUSD, 'USD')}
                  </Typography>
                </Stack>
              </Box>
            );
          }
        },
      ];
    }

    if (orderType === ORDER_TYPES.DISTRIBUTION) {
      columns = [
        ...columns,
        {
          field: 'unit_sale_value', // Field containing per-unit SALE price, BRL-native
          headerName: 'UNIT SALE',
          flex: 1,
          minWidth: 90,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const valueBRL = parseFloat(params.value) || 0;
            const valueUSD = valueBRL / parseFloat(exchangeRate || 1);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                <Stack spacing={0.2} sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {formatCurrency(valueBRL, 'BRL')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                    {formatCurrency(valueUSD, 'USD')}
                  </Typography>
                </Stack>
              </Box>
            );
          }
        },
      ];
    }

    return columns;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Inventory2Icon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">PRODUCTS</Typography>
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGridPremium
            rows={products}
            columns={getColumns()}
            rowHeight={75}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            getRowId={(row) => row.product_item_id}
            sx={{
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
                fontSize: '0.8rem'
              },
              '& .MuiDataGrid-row': {
                width: '100%'
              },
              '@media print': {
                // Hide columns as shown above
                '& .MuiDataGrid-cell[data-field="unit_of_measure"]': {
                  display: 'none !important',
                },
                '& .MuiDataGrid-columnHeader[data-field="unit_of_measure"]': {
                  display: 'none !important',
                },
                '& .MuiDataGrid-cell[data-field="cfr"]': {
                  display: 'none !important',
                },
                '& .MuiDataGrid-columnHeader[data-field="cfr"]': {
                  display: 'none !important',
                },
              }
            }}
              />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductTable;